import { ExternalImage } from '../../Components/ExternalContent';
import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import {
  SOCIAL_NETWORKS,
  emptySocial,
  socialPayload,
  socialsFromApplication,
} from "../../Components/SocialIcons/SocialIcons";
import RegistrationFieldsEditor from "../../Components/RegistrationFieldsEditor";
import apiService from "../../services/api.service";
import { resolveAssetUrl } from "../../utils/assets";
import { normalizeExternalUrl } from "../../utils/urls";
import { applicationUrl } from "../../utils/tenant";
import {
  defaultRegistrationFields,
  fieldsFromApplication,
  registrationFieldsPayload,
  validateRegistrationFields,
} from "../../utils/registrationFields";

const emptyLink = () => ({ label: "", url: "" });
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequiredLabel = ({ children }) => (
  <Form.Label>
    {children}
    <span className="form-required" aria-hidden="true">
      *
    </span>
    <span className="visually-hidden"> (obbligatorio)</span>
  </Form.Label>
);

const apiErrorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((item) => item.msg || item)
      .filter(Boolean)
      .join(" ");
  }
  return fallback;
};

const scrollDashboardToTop = () => {
  const scroller = document.querySelector(".dashboard-content");
  if (scroller) scroller.scrollTop = 0;
};

const DashboardApplicationForm = ({ isNew = false }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [name, setName] = useState("");
  const [siteTitle, setSiteTitle] = useState("");
  const [siteSubtitle, setSiteSubtitle] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#003274");
  const [secondaryColor, setSecondaryColor] = useState("#202a2e");
  const [socials, setSocials] = useState([emptySocial()]);
  const [supportEmail, setSupportEmail] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [footerLinks, setFooterLinks] = useState([emptyLink()]);
  const [registrationFields, setRegistrationFields] = useState(defaultRegistrationFields);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [slug, setSlug] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    const flashSuccess = location.state?.flashSuccess;
    const flashError = location.state?.flashError;
    if (!flashSuccess && !flashError) return;
    if (flashSuccess) setSuccess(flashSuccess);
    if (flashError) setError(flashError);
    history.replace({ pathname: location.pathname, state: {} });
  }, [history, location.pathname, location.state]);

  useEffect(() => {
    if (!error && !success) return;
    const frame = window.requestAnimationFrame(() => {
      scrollDashboardToTop();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [error, success]);

  useEffect(() => {
    if (isNew || !id) return;
    const load = async () => {
      try {
        const apps = await apiService.getApplications();
        const app = (apps.data || []).find((item) => String(item.id) === String(id));
        if (!app) {
          setError("Applicazione non trovata.");
          return;
        }
        setName(app.name || "");
        setSlug(app.slug || "");
        setCustomDomain(app.custom_domain || "");
        setSiteTitle(app.site_title || "");
        setSiteSubtitle(app.site_subtitle || "");
        setPrimaryColor(app.primary_color || "#003274");
        setSecondaryColor(app.secondary_color || "#202a2e");
        const loadedSocials = socialsFromApplication(app);
        setSocials(loadedSocials.length ? loadedSocials : [emptySocial()]);
        setSupportEmail(app.support_email || "");
        setSupportPhone(app.support_phone || "");
        setFooterLinks(app.footer_links?.length ? app.footer_links : [emptyLink()]);
        setRegistrationFields(fieldsFromApplication(app.registration_fields));
        setLogoPreview(resolveAssetUrl(app.logo_url));
      } catch {
        setError("Impossibile caricare l'applicazione.");
      }
    };
    load();
  }, [id, isNew]);

  const usedNetworks = socials.map((item) => item.network).filter(Boolean);

  const updateSocial = (index, field, value) => {
    setSocials((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const addSocial = (index) => {
    if (usedNetworks.length >= SOCIAL_NETWORKS.length) return;
    setSocials((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptySocial());
      return next;
    });
  };

  const removeSocial = (index) => {
    setSocials((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptySocial()];
    });
  };

  const updateFooterLink = (index, field, value) => {
    setFooterLinks((prev) => prev.map((link, i) => (i === index ? { ...link, [field]: value } : link)));
  };

  const addFooterLink = (index) => {
    setFooterLinks((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptyLink());
      return next;
    });
  };

  const removeFooterLink = (index) => {
    setFooterLinks((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyLink()];
    });
  };

  const validateRequiredFields = () => {
    const missing = [];
    if (!name.trim()) missing.push("nome applicazione");
    if (!logoFile && !logoPreview) missing.push("logo");
    if (!siteTitle.trim()) missing.push("titolo sito");
    if (!siteSubtitle.trim()) missing.push("sottotitolo sito");
    if (!supportEmail.trim()) missing.push("email assistenza");
    if (missing.length) {
      return `Compila i campi obbligatori: ${missing.join(", ")}.`;
    }
    if (!EMAIL_PATTERN.test(supportEmail.trim())) {
      return "Inserisci un'email di assistenza valida.";
    }
    const fieldsError = validateRegistrationFields(registrationFields);
    if (fieldsError) return fieldsError;
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const validationError = validateRequiredFields();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    const socialFields = socialPayload(socials);
    const payload = {
      name: name.trim(),
      custom_domain: customDomain.trim().toLowerCase() || null,
      site_title: siteTitle.trim(),
      site_subtitle: siteSubtitle.trim(),
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      ...socialFields,
      support_email: supportEmail.trim(),
      support_phone: supportPhone.trim() || null,
      footer_links: footerLinks
        .filter((link) => link.label.trim() && link.url.trim())
        .map((link) => ({
          label: link.label.trim(),
          url: normalizeExternalUrl(link.url),
        })),
      registration_fields: registrationFieldsPayload(registrationFields),
    };

    try {
      let applicationId = id;
      if (isNew) {
        const created = await apiService.createApplication(payload);
        applicationId = created.data.id;
        setSlug(created.data.slug || "");
      } else {
        await apiService.updateApplication(applicationId, payload);
      }
      if (logoFile) {
        try {
          await apiService.uploadApplicationLogo(applicationId, logoFile);
        } catch (logoErr) {
          if (logoErr.response?.status === 413) {
            setError(
              "Applicazione salvata, ma il logo è troppo grande (errore 413). Riduci l'immagine oppure alza client_max_body_size sul proxy Nginx/NPM, poi ricarica il logo."
            );
            if (isNew) {
              history.replace({ pathname: `/dashboard/applicazioni/${applicationId}` });
            }
            return;
          }
          throw logoErr;
        }
      }
      if (isNew) {
        history.replace({
          pathname: `/dashboard/applicazioni/${applicationId}`,
          state: { flashSuccess: "Applicazione creata con successo." },
        });
        return;
      }
      setSuccess("Applicazione aggiornata.");
    } catch (err) {
      setError(apiErrorMessage(err, "Salvataggio non riuscito."));
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    history.push("/dashboard/applicazioni");
  };

  return (
    <div className="dashboard-card">
      <Link to="/dashboard/applicazioni" className="dashboard-back-link">
        <ArrowLeft size={18} strokeWidth={2} aria-hidden="true" />
        Torna alle applicazioni
      </Link>
      <h1 className="dashboard-card__title">{isNew ? "Nuova applicazione" : "Personalizza applicazione"}</h1>
      {!isNew && slug && (
        <p className="dashboard-card__subtitle">URL pubblico: <a href={applicationUrl({ slug, custom_domain: customDomain.trim().toLowerCase() })}>{applicationUrl({ slug, custom_domain: customDomain.trim().toLowerCase() })}</a></p>
      )}
      <div id="application-form-feedback">
        {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
        {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}
      </div>

      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group className="mb-3" controlId="applicationCustomDomain">
          <Form.Label>Dominio personalizzato</Form.Label>
          <Form.Control value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="corsi.ente.it" autoCapitalize="none" />
          <Form.Text>Inserisci solo il nome host, senza https:// o percorsi. Lascia vuoto per usare il dominio della piattaforma.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="applicationName">
          <RequiredLabel>Nome applicazione (univoco)</RequiredLabel>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            aria-required="true"
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="applicationLogo">
          <RequiredLabel>Logo (apparirà su Navbar e footer)</RequiredLabel>
          {logoPreview && (
            <ExternalImage src={logoPreview} alt="Logo" style={{ height: 64, display: "block", marginBottom: 8 }} />
          )}
          <Form.Control
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            required={isNew || !logoPreview}
            aria-required="true"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setLogoFile(file);
              setLogoPreview(URL.createObjectURL(file));
            }}
          />
          <Form.Text>Formati supportati: PNG, JPG, GIF e WebP.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="applicationSiteTitle">
          <RequiredLabel>Titolo sito</RequiredLabel>
          <Form.Control
            value={siteTitle}
            onChange={(e) => setSiteTitle(e.target.value)}
            required
            aria-required="true"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="applicationSiteSubtitle">
          <RequiredLabel>Sottotitolo sito</RequiredLabel>
          <Form.Control
            value={siteSubtitle}
            onChange={(e) => setSiteSubtitle(e.target.value)}
            required
            aria-required="true"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Colore primario (navbar, pulsanti, dashboard)</Form.Label>
          <Form.Control type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Colore secondario (footer)</Form.Label>
          <Form.Control type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} />
        </Form.Group>

        <Form.Label className="dashboard-section-label">Social</Form.Label>
        {socials.map((social, index) => (
          <div className="dashboard-pair-row" key={`social-${index}`}>
            <Form.Select
              aria-label={`Icona social ${index + 1}`}
              value={social.network}
              onChange={(e) => updateSocial(index, "network", e.target.value)}
            >
              <option value="">Scegli icona</option>
              {SOCIAL_NETWORKS.map((network) => (
                <option
                  key={network.id}
                  value={network.id}
                  disabled={usedNetworks.includes(network.id) && social.network !== network.id}
                >
                  {network.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control
              placeholder="Link"
              value={social.url}
              onChange={(e) => updateSocial(index, "url", e.target.value)}
              aria-label={`Link social ${index + 1}`}
            />
            <button
              type="button"
              className="dashboard-icon-btn"
              title="Rimuovi social"
              aria-label={`Rimuovi social ${index + 1}`}
              onClick={() => removeSocial(index)}
            >
              <Minus size={18} strokeWidth={2} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="dashboard-icon-btn"
              title="Aggiungi social"
              aria-label={`Aggiungi social dopo la riga ${index + 1}`}
              disabled={usedNetworks.length >= SOCIAL_NETWORKS.length}
              onClick={() => addSocial(index)}
            >
              <Plus size={18} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        ))}

        <Form.Label className="dashboard-section-label">Link utili footer</Form.Label>
        {footerLinks.map((link, index) => (
          <div className="dashboard-pair-row" key={`link-${index}`}>
            <Form.Control
              placeholder="Etichetta"
              value={link.label}
              onChange={(e) => updateFooterLink(index, "label", e.target.value)}
            />
            <Form.Control
              placeholder="URL"
              value={link.url}
              onChange={(e) => updateFooterLink(index, "url", e.target.value)}
            />
            <button
              type="button"
              className="dashboard-icon-btn"
              title="Rimuovi link"
              aria-label={`Rimuovi link ${index + 1}`}
              onClick={() => removeFooterLink(index)}
            >
              <Minus size={18} strokeWidth={2} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="dashboard-icon-btn"
              title="Aggiungi link"
              aria-label={`Aggiungi link dopo la riga ${index + 1}`}
              onClick={() => addFooterLink(index)}
            >
              <Plus size={18} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        ))}

        <RegistrationFieldsEditor
          fields={registrationFields}
          onChange={setRegistrationFields}
        />

        <Form.Group className="mb-3 mt-3" controlId="applicationSupportEmail">
          <RequiredLabel>Email assistenza</RequiredLabel>
          <Form.Control
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            required
            aria-required="true"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Telefono assistenza</Form.Label>
          <Form.Control value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
        </Form.Group>

        <div className="dashboard-form-actions">
          <Button type="submit" className="users-edit-btn" disabled={saving}>
            {saving ? "Salvataggio..." : isNew ? "Crea applicazione" : "Salva modifiche"}
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            className="dashboard-cancel-btn"
            disabled={saving}
            onClick={() => setShowCancelModal(true)}
          >
            Annulla
          </Button>
        </div>
      </Form>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Annullare le modifiche?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Sei sicuro di voler uscire? Le modifiche non salvate andranno perse.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>
            Resta qui
          </Button>
          <Button className="users-edit-btn" onClick={confirmCancel}>
            Sì, annulla
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardApplicationForm;
